import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiBriefcase, FiCheckCircle } from 'react-icons/fi';
import { DisclosureCard, useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import { PLANNING_MODULES, PLANNING_OUTCOMES, localizeContent } from '../content/planningContent';
import './FinancialPlanningHub.css';

const COPY = {
  en: {
    title: 'What do you want to understand today?',
    subtitle:
      'Choose a topic and we’ll guide you with clear explanations, tools, and next steps.',
    nextStepTitle: 'Recommended next step',
    nextStepHeading: 'Learn which account fits your goal.',
    nextStepBody: 'Answer a few simple questions and we’ll help you choose.',
    planningTools: 'Planning paths',
    showAll: 'Show all paths',
    showFocus: 'Show fewer paths',
    advancedTitle: 'Advanced portfolio tools',
    advancedBadge: 'Hidden',
    advancedBody:
      'Optimization and analytics are still here, but they work best after you know your goal, timeline, and account type.',
    openAdvanced: 'Open portfolio optimization',
    trustTitle: 'How this stays educational',
    trustSubtitle: 'Assumptions, limits, and professional-advice boundaries.',
    skylineAria: 'Canadian planning illustration',
    trustBullets: [
      'Models are for education and scenario analysis, not guarantees.',
      'Account limits and tax rules should be verified before decisions.',
      'Complex analytics stay hidden until you choose to open them.',
    ],
  },
  fr: {
    title: 'Que voulez-vous comprendre aujourd’hui ?',
    subtitle:
      'Commencez par une seule question. Chaque parcours explique simplement, puis révèle les calculateurs et outils plus détaillés au bon moment.',
    nextStepTitle: 'Prochaine étape recommandée',
    nextStepHeading: 'Apprendre quel compte correspond à votre objectif.',
    nextStepBody: 'Si vous débutez, apprenez quel compte canadien correspond à votre objectif avant de comparer les placements.',
    planningTools: 'Parcours de planification',
    showAll: 'Afficher tous les parcours',
    showFocus: 'Afficher moins de parcours',
    advancedTitle: 'Outils de portefeuille avancés',
    advancedBadge: 'Masqué',
    advancedBody:
      'L’optimisation et les analyses restent disponibles, mais elles sont plus utiles après avoir clarifié votre objectif, l’horizon et le compte.',
    openAdvanced: 'Ouvrir l’optimisation',
    trustTitle: 'Pourquoi cela reste éducatif',
    trustSubtitle: 'Hypothèses, limites et frontières du conseil professionnel.',
    skylineAria: 'Illustration de planification canadienne',
    trustBullets: [
      'Les modèles servent à l’éducation et aux scénarios, pas aux garanties.',
      'Les plafonds et règles fiscales doivent être vérifiés avant les décisions.',
      'Les analyses complexes restent masquées jusqu’à ce que vous les ouvriez.',
    ],
  },
};

const PlanningSkyline = ({ ariaLabel }) => (
  <svg className="planning-skyline" viewBox="0 0 460 150" role="img" aria-label={ariaLabel}>
    <defs>
      <linearGradient id="skylineFade" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0%" stopColor="#dff4f2" stopOpacity="0" />
        <stop offset="50%" stopColor="#dff4f2" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#dff4f2" stopOpacity="0" />
      </linearGradient>
    </defs>
    <ellipse cx="250" cy="82" rx="96" ry="54" fill="url(#skylineFade)" />
    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2">
      <path d="M20 118h420" />
      <path d="M66 118 112 70l34 36 22-24 38 36" />
      <path d="M48 104c12-22 28-22 39 0" />
      <path d="M302 118V50h18v68M328 118V30h16v88M354 118V62h16v56M380 118V74h16v44" />
      <path d="M252 118V42M247 42h10M249 32h6M252 20v12" />
      <path d="M238 76h28M242 92h20" />
      <path d="M206 118V78h24v40M210 90h16M210 102h16" />
      <path d="M158 118V90h22v28M164 90c3-15 12-15 15 0" />
      <path d="M410 118V94h28v24M416 94l8-10 8 10" />
      <path d="M32 118V88M24 96l8-10 8 10M32 88V72M26 78l6-8 6 8" />
      <path d="M58 118V96M51 103l7-9 7 9" />
      <path d="M286 118c-8-16-24-16-32 0" />
      <path d="M172 84c20 20 62 20 84 0" />
      <path d="M178 91c18 12 51 12 70 0" />
      <path d="M26 46c10-13 27-13 37 0 9-7 20-5 26 4" />
      <path d="M394 50c10-12 26-12 36 0 8-6 19-4 24 4" />
      <path d="M154 40c8-10 20-10 28 0 7-5 16-4 20 3" />
    </g>
  </svg>
);

const FinancialPlanningHub = () => {
  const { lang } = useLanguage();
  const c = COPY[lang] || COPY.en;
  const [showAll, setShowAll] = useState(false);

  const visibleModules = useMemo(() => {
    if (showAll) return PLANNING_MODULES.filter((item) => item.id !== 'start');
    return PLANNING_MODULES.filter((item) => ['accounts', 'goals', 'forecast', 'retirement'].includes(item.id));
  }, [showAll]);

  const recommended = PLANNING_MODULES.find((item) => item.id === 'accounts');
  const recommendedCopy = localizeContent(recommended, lang);
  const RecommendedIcon = recommended.icon;

  return (
    <div className="planning-root">
      <section className="planning-hero dashboard-glass">
        <div className="planning-hero-copy">
          <h2 className="planning-title">{c.title}</h2>
          <p className="planning-subtitle">{c.subtitle}</p>
        </div>
        <PlanningSkyline ariaLabel={c.skylineAria} />
        <div className="planning-outcome-list">
          {PLANNING_OUTCOMES.map((item) => {
            const Icon = item.icon;
            const copy = localizeContent(item, lang);
            return (
              <Link key={item.id} to={item.path} className="planning-outcome-row">
                <span className="planning-outcome-icon"><Icon /></span>
                <span>
                  <strong>{copy.title}</strong>
                  <small>{copy.description}</small>
                </span>
                <FiArrowRight />
              </Link>
            );
          })}
        </div>
      </section>

      <ComplianceNotice compact />

      <section className="planning-next-step dashboard-glass">
        <div className="planning-next-icon"><RecommendedIcon /></div>
        <div>
          <p className="planning-next-overline">{c.nextStepTitle}</p>
          <h3>{c.nextStepHeading}</h3>
          <p>{c.nextStepBody}</p>
        </div>
        <Link to={recommended.path} className="planning-next-cta">
          <span>{recommendedCopy.action}</span>
          <FiArrowRight />
        </Link>
      </section>

      <section className="planning-advanced-band dashboard-glass">
        <div className="planning-module-icon muted"><FiBriefcase /></div>
        <div>
          <h3>{c.advancedTitle}</h3>
          <p>{c.advancedBody}</p>
        </div>
        <span className="planning-hidden-badge">{c.advancedBadge}</span>
        <Link to="/portfolio-builder" className="planning-next-cta secondary">{c.openAdvanced}</Link>
      </section>

      <section className="planning-grid-section">
        <div className="planning-section-head">
          <h3>{c.planningTools}</h3>
          <button type="button" className="planning-track-toggle" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? c.showFocus : c.showAll}
          </button>
        </div>

        <div className="planning-module-grid">
          {visibleModules.map((card) => {
            const Icon = card.icon;
            const copy = localizeContent(card, lang);
            return (
              <Link key={card.id} to={card.path} className="planning-module-card dashboard-glass">
                <div className="planning-module-icon">
                  <Icon />
                </div>
                <div>
                  <h4>{copy.title}</h4>
                  <p>{copy.description}</p>
                </div>
                <span className="planning-inline-link">
                  <span>{copy.action}</span>
                  <FiArrowRight />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <DisclosureCard
        title={c.trustTitle}
        subtitle={c.trustSubtitle}
        defaultOpen={false}
        className="planning-trust-disclosure"
      >
        <section className="planning-trust dashboard-glass">
          <ul>
            {c.trustBullets.map((item) => (
              <li key={item}><FiCheckCircle /> <span>{item}</span></li>
            ))}
          </ul>
        </section>
      </DisclosureCard>
    </div>
  );
};

export default FinancialPlanningHub;
